import React from "react";
import {
  useFoodAsync,
  useFoodByUnitAsync,
  usePurchaseAsync,
  usePurchasesByFoodAsync,
} from "../../queries/caches";
import { IngredientGroup, findById } from "../../utils/planUtils";
import { formatPurchaseDescription } from "../../utils/purchaseUtils";
import { ProductPickerDrawer, ProductChoice } from "../ProductPickerDrawer";
import { CreatePurchaseDrawer } from "../CreatePurchaseDrawer";
import { Purchase } from "../../data/purchase";

export function CookingCreatePurchaseDrawer(props: {
  open: boolean;
  onClose: () => void;
  foodUnitFoodId: number | undefined;
  onSubmit: (p: Purchase) => void;
}) {
  const food = useFoodAsync(
    props.foodUnitFoodId && props.foodUnitFoodId > 0
      ? props.foodUnitFoodId
      : undefined,
  );
  return (
    <CreatePurchaseDrawer
      open={props.open}
      onClose={props.onClose}
      food={food}
      onSubmit={props.onSubmit}
    />
  );
}

export function CookingProductPicker(props: {
  open: boolean;
  onClose: () => void;
  group: IngredientGroup;
  activeUnitId: number | undefined;
  activeKind: "food" | "purchase";
  capturedPurchaseId: number | null | undefined;
  onSelect: (id: number | null) => void;
  onOpenCreate: () => void;
}) {
  const { group } = props;
  const firstMi = group.pairs[0].mi;

  const planFood = useFoodAsync(
    group.isFoodBased ? firstMi.foodUnitFoodId : undefined,
  );
  // `activeUnitId` ist nur dann eine FoodUnit-Id, wenn `activeKind === "food"`.
  // Bei einem erfassten Produkt ist es eine PurchaseUnit-Id – dafür das Food
  // über den Purchase auflösen.
  const foodByUnit = useFoodByUnitAsync(
    props.activeKind === "food" ? props.activeUnitId : undefined,
  );
  const capturedPurchase = usePurchaseAsync(
    props.capturedPurchaseId ?? undefined,
  );
  const capturedFood = useFoodAsync(capturedPurchase?.foodId);
  const food =
    props.activeKind === "purchase" ? capturedFood : (foodByUnit ?? planFood);

  const purchases = usePurchasesByFoodAsync(food?.id);
  const relevant = purchases.filter((p) => p.purchaseRelevant);
  const defaultPurchase =
    food?.defaultPurchaseId != null
      ? findById(purchases, food.defaultPurchaseId)
      : undefined;

  const choices: ProductChoice[] = [];
  // Der "Planwert" muss unabhängig vom Ladestand von Food/Purchases immer
  // angeboten werden – sonst fehlt er bei langsamen Requests (Race).
  const planPurchaseId = group.isFoodBased
    ? null
    : group.pairs[0].mi.purchaseUnitPurchaseId;
  const planPurchase =
    planPurchaseId != null ? findById(purchases, planPurchaseId) : undefined;
  choices.push({
    id: null,
    label: group.isFoodBased
      ? "Planwert"
      : formatPurchaseDescription(planPurchase) || group.name,
    kcal: group.isFoodBased ? defaultPurchase?.kcal : planPurchase?.kcal,
  });
  relevant
    .filter((p) =>
      group.isFoodBased
        ? p.id !== defaultPurchase?.id
        : p.id !== planPurchaseId,
    )
    .forEach((p) =>
      choices.push({
        id: p.id,
        label: formatPurchaseDescription(p),
        kcal: p.kcal,
      }),
    );

  return (
    <ProductPickerDrawer
      open={props.open}
      onClose={props.onClose}
      title={food?.description ?? group.name}
      choices={choices}
      selectedId={
        props.capturedPurchaseId === undefined ? null : props.capturedPurchaseId
      }
      baselineKcal={
        group.isFoodBased ? defaultPurchase?.kcal : planPurchase?.kcal
      }
      createFood={food}
      onSelect={props.onSelect}
      onOpenCreate={props.onOpenCreate}
    />
  );
}
